import React, { useEffect, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { applicationActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { ApplicationApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { LoadPanel } from 'devextreme-react/load-panel';
import { ModalSummaryHeader } from '../ApplicationComponents';
import { TabPanel, Item } from 'devextreme-react/tab-panel';
import DataGrid, { Column, Paging, Pager } from 'devextreme-react/data-grid';
import { formatDateTime } from '@apply/ui';
import { ApplicationStatusTypeOptions } from '@apply/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-regular-svg-icons';
import { AuditDetailsDialog } from '../../../common/SharedComponents';

const AuditHistoryModal = () => {
    const { isOpen, id } = useSelector((state) => state.application?.auditHistoryModal);
    const { dispatch } = store;

    const [auditEntries, set_auditEntries] = useState(null);
    const [auditEntry, set_auditEntry] = useState(null);
    const [loadingMessage, set_loadingMessage] = useState('');

    const applicationApi = new ApplicationApi.ApplicationApi(applicationInsights);

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        formRow: { marginBottom: '0' },
        buttonContainer: {
            marginRight: '10px',
            marginLeft: '10px',
            textAlign: 'right',
        },
    };

    const allowedPageSizes = [15, 25, 50];
    const defaultPageSize = 25;

    const _handleClose = () =>
        dispatch(
            applicationActions.setAuditHistoryModal({
                auditHistoryModal: { isOpen: false },
            })
        );

    const loadAuditHistory = async (id) => {
        try {
            set_loadingMessage('Loading...');
            const response = await applicationApi.getAuditHistory(id);
            if (response.statusCode !== 200) {
                return;
            }

            const result = response.result;
            set_auditEntries(result);
        } finally {
            set_loadingMessage('');
        }
    };

    const loadAuditDetail = async (id) => {
        try {
            set_loadingMessage('Loading...');
            const response = await applicationApi.getAuditHistoryDetail(id);
            if (response.statusCode !== 200) {
                return;
            }

            const result = response.result;
            set_auditEntry(result);
        } finally {
            set_loadingMessage('');
        }
    };

    useEffect(() => {
        async function isOpenChanged() {
            resetState();
            if (!isOpen || !id) {
                return;
            }
            await loadAuditHistory(id);
        }

        isOpenChanged();
    }, [isOpen, id]);

    const resetState = () => {
        set_auditEntries(null);
        set_auditEntry(null);
    };

    const renderAddress = (e) => {
        const address = e.value;
        if (!address) {
            return null;
        }
        return (
            <span>
                {address?.street1} {address?.street2} {address?.city}, {address?.state} {address?.zipCode}
            </span>
        );
    };

    const onRenderDateTime = (e) => {
        if (!e || !e.value) {
            return null;
        }
        return formatDateTime(e.value);
    };

    const onStatusRender = (e) => {
        const option = ApplicationStatusTypeOptions.find((o) => o.id === e.value);
        if (!option) {
            return 'Unknown';
        }
        return option.name;
    };

    const showAuditDetail = (event, auditId) => {
        event?.preventDefault();
        event?.stopPropagation();
        loadAuditDetail(auditId);
    };

    const auditActionCellRender = (e) => {
        return (
            <button
                id={'action_' + e.data.id}
                className='gridButton'
                title='View Audit Details...'
                onClick={(event) => showAuditDetail(event, e.data.id)}
            >
                <FontAwesomeIcon icon={faEye} />
            </button>
        );
    };

    const onAuditDetailClose = () => {
        set_auditEntry(null);
    };

    if (!isOpen || !auditEntries) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex='-1'
                            role='dialog'
                            style={{ width: '1000px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div
                                    className='page-header'
                                    role='banner'
                                >
                                    <h1>Application Audit History</h1>
                                    <button
                                        type='button'
                                        className='close'
                                        data-dismiss='modal'
                                        aria-label='Close'
                                        onClick={_handleClose}
                                    >
                                        <span aria-hidden='true'>&times;</span>
                                    </button>
                                </div>
                                <ModalSummaryHeader application={auditEntries} />
                                <div
                                    className='scrollable-content'
                                    style={{ margin: '10px' }}
                                >
                                    <TabPanel
                                        tabsPosition={'left'}
                                        height={300}
                                    >
                                        <Item title='Field Changes'>
                                            <DataGrid
                                                dataSource={auditEntries.changes}
                                                showBorders={true}
                                                showRowLines={false}
                                                showColumnHeaders={true}
                                                rowAlternationEnabled={true}
                                                columnMinWidth={50}
                                                columnAutoWidth={false}
                                                allowSorting={true}
                                                remoteOperations={false}
                                                allowColumnResizing={true}
                                                onRowDblClick={(e) => showAuditDetail(null, e.data.id)}
                                                noDataText='No Audit history available'
                                            >
                                                <Column
                                                    dataField='dateAndTime'
                                                    caption='Timestamp'
                                                    cellRender={onRenderDateTime}
                                                    width={160}
                                                />
                                                <Column
                                                    dataField='userName'
                                                    caption='User'
                                                    alignment='left'
                                                />
                                                <Column
                                                    alignment='center'
                                                    cellRender={auditActionCellRender}
                                                    width={70}
                                                    allowSorting={false}
                                                    allowResizing={false}
                                                />
                                                <Paging defaultPageSize={defaultPageSize} />
                                                <Pager
                                                    showPageSizeSelector={allowedPageSizes.length > 1}
                                                    allowedPageSizes={allowedPageSizes}
                                                    showNavigationButtons={true}
                                                    showInfo={true}
                                                />
                                            </DataGrid>
                                        </Item>
                                        <Item title={'Status History'}>
                                            <div
                                                style={{
                                                    padding: '0 10px 0 10px',
                                                }}
                                            >
                                                <DataGrid
                                                    dataSource={auditEntries.statusChanges}
                                                    showBorders={true}
                                                    showRowLines={false}
                                                    rowAlternationEnabled={true}
                                                    remoteOperations={false}
                                                    allowColumnReordering={false}
                                                    allowColumnResizing={true}
                                                    cellHintEnabled={true}
                                                    noDataText='No Status history available'
                                                >
                                                    <Column
                                                        dataField='statusId'
                                                        caption='Status'
                                                        width={175}
                                                        alignment={'left'}
                                                        cellRender={onStatusRender}
                                                    />
                                                    <Column
                                                        dataField='createdAt'
                                                        caption='On'
                                                        width={150}
                                                        alignment={'left'}
                                                        cellRender={onRenderDateTime}
                                                    />
                                                    <Column
                                                        dataField='createdBy'
                                                        caption='By'
                                                        alignment={'left'}
                                                    />
                                                </DataGrid>
                                            </div>
                                        </Item>
                                        <Item title={'Properties Moved to this Application'}>
                                            <DataGrid
                                                dataSource={auditEntries.propertiesMovedIn}
                                                showBorders={true}
                                                showRowLines={false}
                                                rowAlternationEnabled={true}
                                                remoteOperations={false}
                                                allowColumnReordering={false}
                                                allowColumnResizing={true}
                                                cellHintEnabled={true}
                                                noDataText='No properties reassigned from other Applications'
                                            >
                                                <Column
                                                    dataField='propertyDescription'
                                                    caption='Type'
                                                    width={150}
                                                    alignment={'left'}
                                                />
                                                <Column
                                                    dataField='address'
                                                    caption='Address'
                                                    alignment={'left'}
                                                    cellRender={renderAddress}
                                                />
                                                <Column
                                                    dataField='appDescription'
                                                    caption='From Application'
                                                    width={200}
                                                    alignment={'left'}
                                                />
                                                <Column
                                                    dataField='createdAt'
                                                    caption='On'
                                                    width={150}
                                                    alignment={'left'}
                                                    cellRender={onRenderDateTime}
                                                />
                                                <Column
                                                    dataField='createdBy'
                                                    caption='By'
                                                    width={200}
                                                    alignment={'left'}
                                                />
                                            </DataGrid>
                                        </Item>
                                        <Item title={'Properties Moved to other Applications'}>
                                            <DataGrid
                                                dataSource={auditEntries.propertiesMovedOut}
                                                showBorders={true}
                                                showRowLines={false}
                                                rowAlternationEnabled={true}
                                                remoteOperations={false}
                                                allowColumnReordering={false}
                                                allowColumnResizing={true}
                                                cellHintEnabled={true}
                                                noDataText='No properties reassigned to other Applications'
                                            >
                                                <Column
                                                    dataField='propertyDescription'
                                                    caption='Type'
                                                    width={150}
                                                    alignment={'left'}
                                                />
                                                <Column
                                                    dataField='address'
                                                    caption='Address'
                                                    alignment={'left'}
                                                    cellRender={renderAddress}
                                                />
                                                <Column
                                                    dataField='appDescription'
                                                    caption='To Application'
                                                    width={200}
                                                    alignment={'left'}
                                                />
                                                <Column
                                                    dataField='createdAt'
                                                    caption='On'
                                                    width={150}
                                                    alignment={'left'}
                                                    cellRender={onRenderDateTime}
                                                />
                                                <Column
                                                    dataField='createdBy'
                                                    caption='By'
                                                    width={200}
                                                    alignment={'left'}
                                                />
                                            </DataGrid>
                                        </Item>
                                    </TabPanel>
                                </div>
                                <div
                                    className='button-footer'
                                    style={styles.buttonContainer}
                                >
                                    <button
                                        type='button'
                                        className='button primary'
                                        data-dismiss='modal'
                                        onClick={_handleClose}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={!!loadingMessage}
                message={loadingMessage}
            />
            <AuditDetailsDialog
                isOpen={!!auditEntry}
                auditEntry={auditEntry}
                onClose={onAuditDetailClose}
            />
        </>
    );
};

export default AuditHistoryModal;
